// src/pages/PricingPage.jsx
//
// Uses Stripe Payment Links — zero backend required for checkout.
// When user clicks Subscribe → redirected straight to Stripe-hosted payment page.
//
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../store/hooks';
import { usePremium } from '../hooks/usePremium';
import { STRIPE_PLANS } from '../config/stripePlans';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// ─── Rengigs brand tokens ─────────────────────────────────────────────────────
const R = {
  forestGreen: '#2E7D32',
  deepForest:  '#1B5E20',
  mediumGreen: '#4CAF50',
  lightGreen:  '#8BC34A',
  lime:        '#CDDC39',
  charcoal:    '#1A2A3A',
  offWhite:    '#F8FBF8',
  softGreen:   'rgba(46,125,50,0.07)',
  border:      '#E1E9F0',
  gradient:    'linear-gradient(135deg,#1B5E20 0%,#2E7D32 40%,#4CAF50 70%,#8BC34A 100%)',
  gradientMid: 'linear-gradient(135deg,#4CAF50,#8BC34A)',
};

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}

.rg-root{font-family:'Inter',sans-serif;background:${R.offWhite};color:${R.charcoal};min-height:100vh;padding:3rem 1.5rem 5rem}
.rg-inner{max-width:980px;margin:0 auto}

/* Hero */
.rg-hero{text-align:center;margin-bottom:3.5rem}
.rg-badge{display:inline-flex;align-items:center;gap:.4rem;background:${R.softGreen};color:${R.forestGreen};font-size:.72rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;padding:.3rem .9rem;border-radius:20px;border:1px solid rgba(46,125,50,.2);margin-bottom:1.25rem}
.rg-h1{font-family:'Poppins',sans-serif;font-size:clamp(2rem,5vw,2.8rem);font-weight:800;line-height:1.15;margin-bottom:.85rem;color:${R.charcoal}}
.rg-h1 span{background:linear-gradient(135deg,${R.forestGreen},${R.lightGreen});-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
.rg-sub{color:#4B5563;font-size:1rem;line-height:1.65;max-width:540px;margin:0 auto}

/* Billing toggle */
.rg-toggle{display:inline-flex;align-items:center;background:#fff;border:1px solid ${R.border};border-radius:40px;padding:4px;margin:2rem auto 0;gap:4px}
.rg-toggle-btn{padding:.45rem 1.4rem;border-radius:36px;border:none;font-family:'Inter',sans-serif;font-size:.88rem;font-weight:600;cursor:pointer;transition:all .2s;background:transparent;color:#6B7280}
.rg-toggle-btn.active{background:${R.gradient};color:#fff;box-shadow:0 2px 8px rgba(46,125,50,.3)}
.rg-save-chip{background:rgba(205,220,57,.2);color:${R.deepForest};font-size:.7rem;font-weight:700;padding:.15rem .55rem;border-radius:20px;border:1px solid rgba(205,220,57,.4);margin-left:.5rem}

/* Plan grid */
.rg-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:1.5rem;margin-bottom:3rem}

.rg-card{background:#fff;border:2px solid ${R.border};border-radius:20px;padding:2rem;position:relative;overflow:hidden;transition:border-color .2s,transform .2s,box-shadow .2s;box-shadow:0 2px 12px rgba(0,0,0,.04)}
.rg-card:hover{border-color:${R.mediumGreen};box-shadow:0 8px 32px rgba(46,125,50,.12);transform:translateY(-4px)}
.rg-card.featured{border-color:${R.forestGreen};box-shadow:0 4px 20px rgba(46,125,50,.15)}
.rg-card.featured:hover{border-color:${R.deepForest};box-shadow:0 12px 40px rgba(46,125,50,.22);transform:translateY(-6px)}
.rg-card-accent{position:absolute;top:0;left:0;right:0;height:4px}
.rg-popular{position:absolute;top:1.1rem;right:1.1rem;background:${R.gradient};color:#fff;font-size:.65rem;font-weight:700;letter-spacing:.06em;text-transform:uppercase;padding:.25rem .65rem;border-radius:20px}

.rg-type-badge{display:inline-block;font-size:.68rem;font-weight:700;letter-spacing:.08em;text-transform:uppercase;padding:.25rem .65rem;border-radius:20px;margin-bottom:.9rem}
.rg-plan-name{font-family:'Poppins',sans-serif;font-size:1.2rem;font-weight:700;margin-bottom:.2rem;color:${R.charcoal}}
.rg-plan-tagline{font-size:.82rem;color:#6B7280;margin-bottom:1.4rem}

.rg-price-row{display:flex;align-items:baseline;gap:.4rem;margin-bottom:.3rem}
.rg-amount{font-family:'Poppins',sans-serif;font-size:2.6rem;font-weight:800;line-height:1}
.rg-interval{color:#9CA3AF;font-size:.88rem}
.rg-yearly-note{font-size:.78rem;color:${R.forestGreen};font-weight:600;margin-bottom:1.4rem}

.rg-features{list-style:none;padding:0;margin:0 0 1.75rem;display:flex;flex-direction:column;gap:.6rem}
.rg-feat{display:flex;align-items:flex-start;gap:.6rem;font-size:.88rem;color:#4B5563}
.rg-check{font-weight:700;flex-shrink:0;margin-top:1px;font-size:.9rem}

/* CTA buttons */
.rg-cta{display:block;width:100%;padding:.9rem;border-radius:12px;font-family:'Inter',sans-serif;font-weight:700;font-size:.95rem;text-align:center;text-decoration:none;border:none;cursor:pointer;transition:all .2s;color:#fff}
.rg-cta:hover{transform:translateY(-2px);box-shadow:0 6px 20px rgba(46,125,50,.35);text-decoration:none;color:#fff}
.rg-cta:active{transform:translateY(0)}
.rg-cta-outline{display:block;width:100%;padding:.9rem;border-radius:12px;font-family:'Inter',sans-serif;font-weight:700;font-size:.95rem;text-align:center;text-decoration:none;border:2px solid ${R.forestGreen};cursor:pointer;transition:all .2s;color:${R.forestGreen};background:transparent}
.rg-cta-outline:hover{background:${R.softGreen};transform:translateY(-2px);text-decoration:none;color:${R.forestGreen}}
.rg-cta-current{width:100%;padding:.85rem;border-radius:12px;font-family:'Inter',sans-serif;font-weight:700;font-size:.88rem;text-align:center;background:${R.softGreen};color:${R.forestGreen};border:2px solid rgba(46,125,50,.25)}

/* Current plan bar */
.rg-plan-bar{background:${R.softGreen};border:1px solid rgba(46,125,50,.25);border-radius:16px;padding:1.1rem 1.5rem;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:1rem;margin-bottom:3rem}
.rg-plan-bar-name{font-family:'Poppins',sans-serif;font-weight:700;color:${R.forestGreen};font-size:.95rem;margin-bottom:.2rem}
.rg-plan-bar-sub{font-size:.82rem;color:#4B5563}
.rg-portal-btn{background:transparent;color:${R.forestGreen};border:2px solid ${R.forestGreen};border-radius:10px;padding:.5rem 1.1rem;font-family:'Inter',sans-serif;font-weight:600;font-size:.85rem;cursor:pointer;transition:all .15s;white-space:nowrap}
.rg-portal-btn:hover{background:${R.softGreen}}
.rg-portal-btn:disabled{opacity:.5;cursor:not-allowed}

/* Success */
.rg-success{background:rgba(46,125,50,.07);border:1px solid rgba(46,125,50,.3);border-radius:16px;padding:1.75rem;text-align:center;margin-bottom:2.5rem}
.rg-success-icon{font-size:2.5rem;margin-bottom:.65rem}
.rg-success-title{font-family:'Poppins',sans-serif;font-size:1.15rem;font-weight:700;color:${R.forestGreen};margin-bottom:.4rem}
.rg-success-body{color:#4B5563;font-size:.9rem}

/* Divider + table */
.rg-divider{height:1px;background:${R.border};margin:2.5rem 0}
.rg-section-title{font-family:'Poppins',sans-serif;font-size:1.05rem;font-weight:700;margin-bottom:1.25rem;color:${R.charcoal}}
.rg-table{width:100%;border-collapse:collapse;font-size:.88rem;background:#fff;border-radius:16px;overflow:hidden;box-shadow:0 2px 12px rgba(0,0,0,.04)}
.rg-table th{text-align:left;font-size:.72rem;font-weight:700;letter-spacing:.07em;text-transform:uppercase;padding:.75rem 1rem;border-bottom:2px solid ${R.border};background:${R.softGreen};color:#6B7280}
.rg-table th:first-child{color:${R.charcoal}}
.rg-table td{padding:.7rem 1rem;border-bottom:1px solid ${R.border};color:#4B5563;vertical-align:middle}
.rg-table td:first-child{color:${R.charcoal};font-weight:500}
.rg-table tr:last-child td{border-bottom:none}
.rg-table tr:hover td{background:${R.softGreen}}
.rg-yes{color:${R.forestGreen};font-weight:700}
.rg-no{color:#D1D5DB}

/* Error / footnote */
.rg-error{background:rgba(239,68,68,.07);border:1px solid rgba(239,68,68,.25);border-radius:10px;padding:.85rem 1rem;color:#DC2626;font-size:.85rem;margin-bottom:1rem;text-align:center}
.rg-footnote{text-align:center;margin-top:2.5rem;color:#9CA3AF;font-size:.8rem;line-height:1.6}

/* Not-logged-in banner */
.rg-login-bar{background:#fff;border:1px solid ${R.border};border-radius:14px;padding:1rem 1.5rem;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:1rem;margin-bottom:2rem;box-shadow:0 2px 8px rgba(0,0,0,.04)}
.rg-login-bar-text{font-size:.9rem;color:#4B5563}
.rg-login-bar-text strong{color:${R.forestGreen}}
`;

// ─── Plan card definitions ────────────────────────────────────────────────────
const PLANS_CONFIG = [
  {
    type:       'vendor',
    name:       'Vendor Premium',
    tagline:    'For suppliers, vendors & solution providers',
    color:      R.forestGreen,
    accentGrad: R.gradient,
    badgeBg:    R.softGreen,
    badgeColor: R.forestGreen,
    featured:   true,
    features: [
      'Unlimited direct messages per month',
      'Submit bids directly to any problem',
      '30-day solution viewer history & analytics',
      'Solutions across all industries & sectors',
      'Competitor benchmark insights',
    ],
  },
  {
    type:       'organization',
    name:       'Organisation Premium',
    tagline:    'For organisations posting problems',
    color:      R.mediumGreen,
    accentGrad: R.gradientMid,
    badgeBg:    'rgba(76,175,80,.1)',
    badgeColor: R.mediumGreen,
    featured:   false,
    features: [
      'Unlimited direct messages per month',
      'See all tagged solutions per problem',
      'AI best-match solution insight',
      'Solutions across all industries & sectors',
    ],
  },
];

const TABLE_ROWS = [
  { f: 'Direct messages / month',          free: '5',        vendor: '✓ Unlimited', org: '✓ Unlimited' },
  { f: 'Bids submitted / month',            free: '5',        vendor: '✓ Unlimited', org: '—'           },
  { f: 'Tagged solutions per problem',      free: 'First 5',  vendor: '✓ All',       org: '✓ All'       },
  { f: 'Solution viewer history (30 days)', free: '✗',        vendor: '✓',           org: '—'           },
  { f: 'Cross-sector discovery',            free: '✗',        vendor: '✓',           org: '✓'           },
  { f: 'Competitor insights',               free: '✗',        vendor: '✓',           org: '—'           },
  { f: 'AI best-match insight',             free: '✗',        vendor: '—',           org: '✓'           },
];

function TableCell({ val }) {
  if (val === '✗') return <span className="rg-no">✗</span>;
  if (val.startsWith('✓')) return <span className="rg-yes">{val}</span>;
  if (val === '—') return <span style={{ color: '#D1D5DB' }}>—</span>;
  return <span>{val}</span>;
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function PricingPage() {
  const navigate                    = useNavigate();
  const { user, isAuthenticated }   = useAppSelector(s => s.auth);
  const { isPremium, planType, subscription, refresh } = usePremium();

  const [billing, setBilling]       = useState('monthly');   // 'monthly' | 'yearly'
  const [portalLoad, setPortalLoad] = useState(false);
  const [error, setError]           = useState('');

  const isSuccess = window.location.pathname.includes('/billing/success');

  useEffect(() => {
    if (isSuccess) refresh();
  }, [isSuccess, refresh]);

  // ── Stripe Payment Link redirect ──────────────────────────────────────────
  const handleSubscribe = (planType) => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/pricing' } });
      return;
    }
    const planData = STRIPE_PLANS[planType]?.[billing];
    if (!planData?.paymentLink || planData.paymentLink.includes('REPLACE')) {
      setError('Payment link not configured. Please contact support.');
      return;
    }
    // Append prefilled email so Stripe pre-fills the checkout form
    const email   = user?.email || '';
    const baseUrl = planData.paymentLink;
    const url     = email
      ? `${baseUrl}?prefilled_email=${encodeURIComponent(email)}`
      : baseUrl;
    window.location.href = url;
  };

  // ── Stripe Portal ─────────────────────────────────────────────────────────
  const handlePortal = async () => {
    setPortalLoad(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${API}/billing/portal/`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
      );
      window.location.href = data.portal_url;
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not open billing portal. Please try again.');
      setPortalLoad(false);
    }
  };

  const isCurrentPlan = (type) =>
    isPremium && planType === `${type}_premium`;

  return (
    <>
      <style>{CSS}</style>
      <div className="rg-root">
        <div className="rg-inner">

          {/* Success */}
          {isSuccess && (
            <div className="rg-success">
              <div className="rg-success-icon">🎉</div>
              <div className="rg-success-title">You're now on Premium!</div>
              <div className="rg-success-body">
                Your subscription is active. All premium features are unlocked.
              </div>
            </div>
          )}

          {/* Active plan bar */}
          {isPremium && subscription && (
            <div className="rg-plan-bar">
              <div>
                <div className="rg-plan-bar-name">✓ Active: {subscription.plan?.name}</div>
                <div className="rg-plan-bar-sub">
                  Renews{' '}
                  {new Date(subscription.current_period_end).toLocaleDateString('en-GB', {
                    day: 'numeric', month: 'long', year: 'numeric',
                  })}
                  {subscription.cancel_at_period_end && ' · Cancels at period end'}
                </div>
              </div>
              <button className="rg-portal-btn" onClick={handlePortal} disabled={portalLoad}>
                {portalLoad ? 'Loading…' : 'Manage Subscription →'}
              </button>
            </div>
          )}

          {/* Not logged in nudge */}
          {!isAuthenticated && (
            <div className="rg-login-bar">
              <div className="rg-login-bar-text">
                <strong>Sign in</strong> to subscribe — your email will be pre-filled at checkout.
              </div>
              <button
                onClick={() => navigate('/login', { state: { from: '/pricing' } })}
                style={{ background: R.gradient, color: '#fff', border: 'none', borderRadius: 10, padding: '.5rem 1.25rem', fontFamily: 'Inter,sans-serif', fontWeight: 700, fontSize: '.88rem', cursor: 'pointer' }}
              >
                Sign In →
              </button>
            </div>
          )}

          {/* Hero */}
          <div className="rg-hero">
            <div className="rg-badge">⭐ Rengigs Premium</div>
            <h1 className="rg-h1">Unlock the full <span>Rengigs</span> ecosystem</h1>
            <p className="rg-sub">
              Connect deeper, discover more, and act faster with a plan built for your role.
            </p>

            {/* Monthly / Yearly toggle */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div className="rg-toggle">
                <button
                  className={`rg-toggle-btn${billing === 'monthly' ? ' active' : ''}`}
                  onClick={() => setBilling('monthly')}
                >
                  Monthly
                </button>
                <button
                  className={`rg-toggle-btn${billing === 'yearly' ? ' active' : ''}`}
                  onClick={() => setBilling('yearly')}
                >
                  Yearly
                  <span className="rg-save-chip">Save $40</span>
                </button>
              </div>
            </div>
          </div>

          {error && <p className="rg-error">{error}</p>}

          {/* Plan cards */}
          <div className="rg-grid">
            {PLANS_CONFIG.map(plan => {
              const priceData    = STRIPE_PLANS[plan.type]?.[billing];
              const alreadyOnIt  = isCurrentPlan(plan.type);

              return (
                <div key={plan.type} className={`rg-card${plan.featured ? ' featured' : ''}`}>
                  <div className="rg-card-accent" style={{ background: plan.accentGrad }} />
                  {plan.featured && <div className="rg-popular">Most popular</div>}

                  <div className="rg-type-badge" style={{ background: plan.badgeBg, color: plan.badgeColor }}>
                    {plan.type === 'vendor' ? 'Vendors' : 'Organisations'}
                  </div>

                  <div className="rg-plan-name">{plan.name}</div>
                  <div className="rg-plan-tagline">{plan.tagline}</div>

                  <div className="rg-price-row">
                    <span className="rg-amount" style={{ color: plan.color }}>
                      {priceData?.price || '—'}
                    </span>
                    <span className="rg-interval">/ {priceData?.interval || 'month'}</span>
                  </div>

                  {billing === 'yearly' && (
                    <div className="rg-yearly-note">
                      🎉 2 months free vs monthly billing
                    </div>
                  )}

                  <ul className="rg-features">
                    {plan.features.map(f => (
                      <li key={f} className="rg-feat">
                        <span className="rg-check" style={{ color: plan.color }}>✓</span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {alreadyOnIt ? (
                    <div className="rg-cta-current">✓ Your current plan</div>
                  ) : plan.featured ? (
                    <button
                      className="rg-cta"
                      style={{ background: plan.accentGrad }}
                      onClick={() => handleSubscribe(plan.type)}
                    >
                      Subscribe — {priceData?.price}/{priceData?.interval}
                    </button>
                  ) : (
                    <button
                      className="rg-cta-outline"
                      onClick={() => handleSubscribe(plan.type)}
                    >
                      Subscribe — {priceData?.price}/{priceData?.interval}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="rg-divider" />

          {/* Comparison table */}
          <div className="rg-section-title">Full feature comparison</div>
          <table className="rg-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Free</th>
                <th style={{ color: R.forestGreen }}>Vendor Premium</th>
                <th style={{ color: R.mediumGreen }}>Org Premium</th>
              </tr>
            </thead>
            <tbody>
              {TABLE_ROWS.map(row => (
                <tr key={row.f}>
                  <td>{row.f}</td>
                  <td><TableCell val={row.free} /></td>
                  <td><TableCell val={row.vendor} /></td>
                  <td><TableCell val={row.org} /></td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="rg-footnote">
            No long-term contracts · Cancel any time · Payments secured by Stripe<br />
            After payment you'll be redirected back to Rengigs automatically.
          </p>

        </div>
      </div>
    </>
  );
}
